import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const FeaturesBuilder = ({ features, benefits, onUpdateFeatures, onUpdateBenefits }) => {
  const [newFeature, setNewFeature] = useState('');
  const [newBenefit, setNewBenefit] = useState('');

  const handleAddFeature = () => {
    if (newFeature?.trim()) {
      onUpdateFeatures([...features, newFeature?.trim()]);
      setNewFeature('');
    }
  };

  const handleRemoveFeature = (index) => {
    onUpdateFeatures(features?.filter((_, i) => i !== index));
  };

  const handleAddBenefit = () => {
    if (newBenefit?.trim()) {
      onUpdateBenefits([...benefits, newBenefit?.trim()]);
      setNewBenefit('');
    }
  };

  const handleRemoveBenefit = (index) => {
    onUpdateBenefits(benefits?.filter((_, i) => i !== index));
  };

  return (
    <div className="space-y-8">
      {/* Features Section */}
      <div>
        <div className="flex items-center gap-2 mb-4">
          <Icon name="List" size={24} color="var(--color-primary)" />
          <h3 className="text-lg font-heading font-semibold text-foreground">Plan Features</h3>
        </div>
        <p className="text-sm text-muted-foreground mb-4">
          Add specific features included in this membership plan (e.g., equipment access, facility amenities)
        </p>

        <div className="flex gap-2 mb-4">
          <Input
            placeholder="Enter a feature..."
            value={newFeature}
            onChange={(e) => setNewFeature(e?.target?.value)}
            onKeyPress={(e) => e?.key === 'Enter' && handleAddFeature()}
          />
          <Button onClick={handleAddFeature} iconName="Plus" disabled={!newFeature?.trim()}>
            Add
          </Button>
        </div>

        <div className="space-y-2">
          {features?.length === 0 ? (
            <div className="text-center py-8 bg-muted/50 rounded-lg">
              <Icon name="Package" size={32} className="text-muted-foreground mx-auto mb-2" />
              <p className="text-sm text-muted-foreground">No features added yet</p>
            </div>
          ) : (
            features?.map((feature, index) => (
              <div
                key={index}
                className="flex items-center justify-between p-3 bg-muted/50 rounded-lg hover:bg-muted transition-colors"
              >
                <div className="flex items-center gap-3">
                  <Icon name="Check" size={18} color="var(--color-success)" />
                  <span className="text-sm text-foreground">{feature}</span>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleRemoveFeature(index)}
                  iconName="X"
                />
              </div>
            ))
          )}
        </div>
      </div>
      {/* Benefits Section */}
      <div className="pt-6 border-t border-border">
        <div className="flex items-center gap-2 mb-4">
          <Icon name="Gift" size={24} color="var(--color-accent)" />
          <h3 className="text-lg font-heading font-semibold text-foreground">Member Benefits</h3>
        </div>
        <p className="text-sm text-muted-foreground mb-4">
          Add benefits and perks members receive with this plan (e.g., discounts, priority booking)
        </p>

        <div className="flex gap-2 mb-4">
          <Input
            placeholder="Enter a benefit..."
            value={newBenefit}
            onChange={(e) => setNewBenefit(e?.target?.value)}
            onKeyPress={(e) => e?.key === 'Enter' && handleAddBenefit()}
          />
          <Button onClick={handleAddBenefit} iconName="Plus" disabled={!newBenefit?.trim()}>
            Add
          </Button>
        </div>

        <div className="space-y-2">
          {benefits?.length === 0 ? (
            <div className="text-center py-8 bg-muted/50 rounded-lg">
              <Icon name="Gift" size={32} className="text-muted-foreground mx-auto mb-2" />
              <p className="text-sm text-muted-foreground">No benefits added yet</p>
            </div>
          ) : (
            benefits?.map((benefit, index) => (
              <div
                key={index}
                className="flex items-center justify-between p-3 bg-muted/50 rounded-lg hover:bg-muted transition-colors"
              >
                <div className="flex items-center gap-3">
                  <Icon name="Star" size={18} color="var(--color-accent)" />
                  <span className="text-sm text-foreground">{benefit}</span>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleRemoveBenefit(index)}
                  iconName="X"
                />
              </div>
            ))
          )}
        </div>
      </div>
      {/* Quick Add Suggestions */}
      <div className="pt-6 border-t border-border">
        <h4 className="text-sm font-semibold text-muted-foreground mb-3">Quick Add Suggestions</h4>
        <div className="flex flex-wrap gap-2">
          {['24/7 gym access', 'Unlimited group classes', 'Personal locker', 'Guest passes', 'Nutrition consultation', 'Free parking']?.map(
            (suggestion) => (
              <button
                key={suggestion}
                onClick={() => {
                  if (!features?.includes(suggestion)) {
                    onUpdateFeatures([...features, suggestion]);
                  }
                }}
                className="px-3 py-1 bg-primary/10 text-primary rounded-full text-xs hover:bg-primary/20 transition-colors"
              >
                + {suggestion}
              </button>
            )
          )}
        </div>
      </div>
    </div>
  );
};

export default FeaturesBuilder;